/**
 * useLayoutSync - Syncs tile layout between client and server.
 *
 * Follows the same pattern as SettingsPage: merge tileLayout into the
 * existing user_settings JSON blob via `/api/settings`.
 *
 * Flow:
 *  1. If a cached token exists, fetch settings IMMEDIATELY (optimistic).
 *     Otherwise wait for auth to complete, then fetch.
 *  2. If server has tileLayout, apply it to the homescreen store
 *     (server = source of truth, overrides localStorage cache)
 *  3. If the optimistic fetch fails (token expired) and auth later
 *     succeeds with a fresh token, retry the fetch automatically.
 *  4. When user drags tiles, debounce-save the new layout to server
 *  5. Optimistic: localStorage updates instantly, server syncs async
 *
 * The tileLayout lives inside the same `settings_json` field as
 * displayName, theme, etc. — keeping one table, one endpoint.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useHomeScreenStore, type TileLayout } from '@room/shared-ui';
import { getSessionToken } from '@room/frontend-sdk';

const SAVE_DEBOUNCE_MS = 1500;

interface SettingsPayload {
  settings: Record<string, unknown>;
  updatedAt: number | null;
}

async function fetchSettings(): Promise<SettingsPayload | null> {
  const token = getSessionToken();
  if (!token) return null;

  try {
    const res = await fetch('/api/settings', {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (!res.ok) return null;
    const json = await res.json();
    if (!json.success) return null;
    return json.data as SettingsPayload;
  } catch {
    return null;
  }
}

async function saveSettings(
  settings: Record<string, unknown>,
  updatedAt: number | null
): Promise<number | null> {
  const token = getSessionToken();
  if (!token) return null;

  try {
    const res = await fetch('/api/settings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        settings,
        updatedAt: updatedAt ?? Date.now(),
      }),
    });
    if (!res.ok) return null;
    const json = await res.json();
    if (!json.success) return null;
    return (json.data as SettingsPayload).updatedAt ?? null;
  } catch {
    return null;
  }
}

/**
 * @param authDone - True only when auth phase is 'done' (not 'idle').
 *   This prevents premature fetching when auth hasn't been attempted yet
 *   (initial idle state) vs when auth has completed successfully.
 * @returns synced - True once server layout has been fetched and applied,
 *   or if auth hasn't succeeded (no server data to load).
 */
export function useLayoutSync(authDone: boolean): boolean {
  const tileLayout = useHomeScreenStore((s) => s.tileLayout);
  const serverTimestamp = useRef<number | null>(null);
  const serverSettings = useRef<Record<string, unknown>>({});
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevLayoutJson = useRef<string>('');

  // Track fetch lifecycle: whether we've attempted and whether it succeeded.
  const hasFetched = useRef(false);
  const fetchSucceeded = useRef(false);

  // Track when the initial fetch actually completes (not just starts).
  // Using state so the watcher effect re-evaluates when this flips to true.
  const [fetchDone, setFetchDone] = useState(false);

  // Check for cached token once at mount — if present, we can start
  // fetching settings immediately without waiting for auth validation.
  const hasCachedToken = useRef(!!getSessionToken()).current;

  // Shared fetch-and-apply logic used by both optimistic and post-auth paths.
  const applySettings = useCallback((payload: SettingsPayload | null) => {
    if (!payload) {
      // No token or fetch failed — seed prevLayoutJson with local state
      // so the watcher doesn't immediately fire a spurious save.
      prevLayoutJson.current = JSON.stringify(
        useHomeScreenStore.getState().tileLayout
      );
      return false; // did not succeed
    }

    serverTimestamp.current = payload.updatedAt ?? null;
    serverSettings.current = payload.settings ?? {};

    const serverLayout = payload.settings?.tileLayout;
    if (serverLayout && typeof serverLayout === 'object') {
      const layout = serverLayout as TileLayout;

      // Apply server layout to store (overrides localStorage cache)
      const store = useHomeScreenStore.getState();
      const currentLayout = store.tileLayout;
      const serverJson = JSON.stringify(layout);
      const localJson = JSON.stringify(currentLayout);

      if (serverJson !== localJson) {
        // Server is source of truth - apply it
        useHomeScreenStore.setState({ tileLayout: layout });

        // Re-register all tiles in a single batch so they pick up server
        // positions without dispatching N separate store updates (which
        // would cause the home grid to re-render N times in a row).
        store.registerTiles(Array.from(store.items.values()));
      }

      prevLayoutJson.current = serverJson;
    } else {
      prevLayoutJson.current = JSON.stringify(
        useHomeScreenStore.getState().tileLayout
      );
    }

    return true; // succeeded
  }, []);

  // Step 1a: Optimistic fetch — start immediately if cached token exists.
  // This runs in parallel with auth validation, saving one round-trip
  // for returning users whose token is still valid.
  useEffect(() => {
    if (!hasCachedToken || hasFetched.current) return;
    hasFetched.current = true;

    (async () => {
      const payload = await fetchSettings();
      const ok = applySettings(payload);
      fetchSucceeded.current = ok;
      setFetchDone(true);
    })();
  }, [hasCachedToken, applySettings]);

  // Step 1b: Post-auth fetch — if auth succeeds but the optimistic fetch
  // failed (expired token returned 401), retry with the fresh token.
  useEffect(() => {
    if (!authDone) return;
    // If we never tried or already succeeded, nothing to do
    if (!hasFetched.current) {
      // No cached token existed — this is the first fetch
      hasFetched.current = true;
      (async () => {
        const payload = await fetchSettings();
        const ok = applySettings(payload);
        fetchSucceeded.current = ok;
        setFetchDone(true);
      })();
      return;
    }
    if (fetchSucceeded.current) return;

    // Optimistic fetch failed and auth has now succeeded — retry
    (async () => {
      setFetchDone(false);
      const payload = await fetchSettings();
      const ok = applySettings(payload);
      fetchSucceeded.current = ok;
      setFetchDone(true);
    })();
  }, [authDone, applySettings]);

  // Step 2: Debounce-save to server when layout changes
  const saveToServer = useCallback((layout: TileLayout) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(async () => {
      // Merge tileLayout into existing settings (preserve other fields)
      const merged = {
        ...serverSettings.current,
        tileLayout: layout,
      };

      const newTimestamp = await saveSettings(merged, serverTimestamp.current);
      if (newTimestamp !== null) {
        serverTimestamp.current = newTimestamp;
        serverSettings.current = merged;
      }
    }, SAVE_DEBOUNCE_MS);
  }, []);

  // Watch for layout changes — only after the initial fetch completes.
  // Using `fetchDone` (state) instead of a ref ensures this effect
  // re-runs once the fetch finishes and `prevLayoutJson` is initialised,
  // preventing the spurious save that would overwrite server data.
  useEffect(() => {
    if (!fetchDone) return;

    const currentJson = JSON.stringify(tileLayout);
    if (currentJson === prevLayoutJson.current) return;

    prevLayoutJson.current = currentJson;
    saveToServer(tileLayout);
  }, [tileLayout, saveToServer, fetchDone]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return fetchDone;
}
